import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, UserPlus, Trash2, Copy, Mail, Users, AtSign, Plus, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateSubAdminModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '', generate_password: false });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formData, password: formData.generate_password ? null : formData.password };
      const { data } = await axios.post(`${API}/master-admin/admins`, payload, { withCredentials: true });
      if (data.password) {
        setGeneratedPassword(data.password);
      } else {
        toast.success('Sub-admin created');
        onClose();
        onCreated();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    toast.success('Password copied');
  };

  const inputStyle = { backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
      <div className="w-full max-w-md p-6 rounded-2xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
        <div className="flex items-center gap-2 mb-6">
          <Shield size={24} style={{ color: '#3B82F6' }} />
          <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Create Sub-Admin</h2>
        </div>

        {generatedPassword ? (
          <div>
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid #3B82F6' }}>
              <p className="text-sm mb-2" style={{ color: '#DDDDDD' }}>Sub-admin created! Password:</p>
              <div className="flex items-center gap-2">
                <p className="font-mono text-lg font-bold flex-1" style={{ color: '#3B82F6' }}>{generatedPassword}</p>
                <button onClick={copyPassword} className="p-2 rounded-lg" style={{ backgroundColor: '#2A3F52' }}>
                  <Copy size={16} color="white" />
                </button>
              </div>
              <p className="text-xs mt-2" style={{ color: '#8197A4' }}>Save this password. It won&apos;t be shown again.</p>
            </div>
            <button onClick={() => { onClose(); onCreated(); setGeneratedPassword(''); setFormData({ name: '', email: '', password: '', generate_password: false }); }}
              className="w-full py-3 rounded-lg font-semibold" style={{ backgroundColor: '#1E3A8A', color: 'white' }}
              data-testid="close-sub-admin-modal">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Full Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required data-testid="sub-admin-name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Email</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required data-testid="sub-admin-email" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.generate_password}
                onChange={(e) => setFormData({ ...formData, generate_password: e.target.checked, password: '' })}
                className="w-4 h-4" data-testid="generate-sub-admin-password" />
              <span className="text-sm" style={{ color: '#DDDDDD' }}>Generate secure random password</span>
            </label>
            {!formData.generate_password && (
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Password</label>
                <input type="text" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required minLength="6"
                  data-testid="sub-admin-password" />
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#2A3F52', color: '#FFFFFF' }}>Cancel</button>
              <button type="submit" disabled={submitting} className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#1E3A8A', color: 'white' }} data-testid="submit-sub-admin">
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const CreateMemberModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '', membership_id: '', date_of_birth: '', email: '', password: '', generate_password: false
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        membership_id: formData.membership_id,
        date_of_birth: formData.date_of_birth,
        email: formData.email || null,
        password: formData.generate_password ? null : formData.password,
        generate_password: formData.generate_password
      };
      const { data } = await axios.post(`${API}/master-admin/members`, payload, { withCredentials: true });
      if (data.password) {
        setGeneratedPassword(data.password);
      } else {
        toast.success('Member created');
        onClose();
        onCreated();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    toast.success('Password copied');
  };

  const resetAndClose = () => {
    onClose();
    onCreated();
    setGeneratedPassword('');
    setFormData({ name: '', membership_id: '', date_of_birth: '', email: '', password: '', generate_password: false });
  };

  const inputStyle = { backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
      <div className="w-full max-w-md p-6 rounded-2xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
        <div className="flex items-center gap-2 mb-6">
          <Users size={24} style={{ color: '#3B82F6' }} />
          <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Create Member</h2>
        </div>

        {generatedPassword ? (
          <div>
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid #3B82F6' }}>
              <p className="text-sm mb-2" style={{ color: '#DDDDDD' }}>Member created! Password:</p>
              <div className="flex items-center gap-2">
                <p className="font-mono text-lg font-bold flex-1" style={{ color: '#3B82F6' }}>{generatedPassword}</p>
                <button onClick={copyPassword} className="p-2 rounded-lg" style={{ backgroundColor: '#2A3F52' }}>
                  <Copy size={16} color="white" />
                </button>
              </div>
              <p className="text-xs mt-2" style={{ color: '#8197A4' }}>Save this password. It won&apos;t be shown again.</p>
            </div>
            <button onClick={resetAndClose}
              className="w-full py-3 rounded-lg font-semibold" style={{ backgroundColor: '#1E3A8A', color: 'white' }}
              data-testid="close-member-modal">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Full Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required data-testid="member-name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Membership ID</label>
              <input type="text" value={formData.membership_id} onChange={(e) => setFormData({ ...formData, membership_id: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required data-testid="member-membership-id" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Date of Birth</label>
              <input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required data-testid="member-dob" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Email (optional)</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} data-testid="member-email" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.generate_password}
                onChange={(e) => setFormData({ ...formData, generate_password: e.target.checked, password: '' })}
                className="w-4 h-4" data-testid="generate-member-password" />
              <span className="text-sm" style={{ color: '#DDDDDD' }}>Generate secure random password</span>
            </label>
            {!formData.generate_password && (
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Password</label>
                <input type="text" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle} required minLength="6"
                  data-testid="member-password" />
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#2A3F52', color: '#FFFFFF' }}>Cancel</button>
              <button type="submit" disabled={submitting} className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#1E3A8A', color: 'white' }} data-testid="submit-member">
                {submitting ? 'Creating...' : 'Create Member'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export const MasterAdminPage = () => {
  const [admins, setAdmins] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [emailConfig, setEmailConfig] = useState({ primary_email: '', secondary_emails: [] });
  const [newSecEmail, setNewSecEmail] = useState('');
  const [addingSecEmail, setAddingSecEmail] = useState(false);

  useEffect(() => { fetchAdmins(); fetchMembers(); fetchEmails(); }, []);

  const fetchAdmins = async () => {
    try {
      const { data } = await axios.get(`${API}/master-admin/admins`, { withCredentials: true });
      setAdmins(data);
    } catch (error) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const { data } = await axios.get(`${API}/master-admin/members`, { withCredentials: true });
      setMembers(data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const fetchEmails = async () => {
    try {
      const { data } = await axios.get(`${API}/master-admin/account/emails`, { withCredentials: true });
      setEmailConfig(data);
    } catch (error) {
      console.error('Failed to fetch account emails:', error);
    }
  };

  const handleAddSecondaryEmail = async (e) => {
    e.preventDefault();
    if (!newSecEmail.trim()) return;
    setAddingSecEmail(true);
    try {
      await axios.post(`${API}/master-admin/account/emails`, { email: newSecEmail.trim() }, { withCredentials: true });
      toast.success('Secondary email added');
      setNewSecEmail('');
      fetchEmails();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add');
    } finally {
      setAddingSecEmail(false);
    }
  };

  const handleRemoveSecondaryEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from secondary emails?`)) return;
    try {
      await axios.delete(`${API}/master-admin/account/emails/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success('Secondary email removed');
      fetchEmails();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleDeleteAdmin = async (userId, name) => {
    if (!window.confirm(`Remove sub-admin ${name}?`)) return;
    try {
      await axios.delete(`${API}/master-admin/admins/${userId}`, { withCredentials: true });
      toast.success('Sub-admin removed');
      fetchAdmins();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to remove');
    }
  };

  const handleDeleteMember = async (userId, name) => {
    if (!window.confirm(`Delete member ${name}?`)) return;
    try {
      await axios.delete(`${API}/master-admin/members/${userId}`, { withCredentials: true });
      toast.success('Member deleted');
      fetchMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  const masterAdmin = admins.find(a => a.role === 'master_admin');
  const subAdmins = admins.filter(a => a.role === 'admin');

  return (
    <div className="min-h-screen pt-24 px-4 sm:px-8 md:px-12 lg:px-16 pb-12" style={{ backgroundColor: '#0F171E' }} data-testid="master-admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Shield size={32} style={{ color: '#3B82F6' }} />
          <h1 className="text-4xl font-bold" style={{ color: '#FFFFFF' }}>Master Admin Console</h1>
        </div>
        <p className="mb-8" style={{ color: '#8197A4' }}>Manage sub-administrators and members</p>

        {masterAdmin && (
          <div className="mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'rgba(240, 193, 75, 0.08)', border: '2px solid #F0C14B' }}>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0C14B' }}>
                <Shield size={24} color="#0F171E" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold" style={{ color: '#FFFFFF' }}>{masterAdmin.name}</h3>
                  <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{ backgroundColor: '#F0C14B', color: '#0F171E' }}>
                    MASTER ADMIN
                  </span>
                </div>
                <p className="text-sm" style={{ color: '#DDDDDD' }}>{masterAdmin.email}</p>
                <p className="text-xs mt-1" style={{ color: '#8197A4' }}>{masterAdmin.membership_id}</p>
              </div>
            </div>
          </div>
        )}

        {/* Account Settings - Secondary Emails */}
        <div className="rounded-2xl p-6 mb-8" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="master-account-settings">
          <div className="flex items-center gap-3 mb-2">
            <AtSign size={22} style={{ color: '#3B82F6' }} />
            <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Account Settings</h2>
          </div>
          <p className="text-sm mb-5" style={{ color: '#8197A4' }}>
            Add multiple emails to use for password reset and login. Any of these emails plus your Membership ID will work at the login screen.
          </p>

          <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52' }}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#3B82F6' }}>
                PRIMARY
              </span>
              <span className="font-mono text-sm" style={{ color: '#FFFFFF' }}>{emailConfig.primary_email}</span>
            </div>
          </div>

          {emailConfig.secondary_emails.length > 0 && (
            <div className="space-y-2 mb-4">
              {emailConfig.secondary_emails.map((em) => (
                <div key={em} className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52' }}
                  data-testid={`secondary-email-${em}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981' }}>
                      SECONDARY
                    </span>
                    <span className="font-mono text-sm" style={{ color: '#FFFFFF' }}>{em}</span>
                  </div>
                  <button onClick={() => handleRemoveSecondaryEmail(em)}
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444', border: '1px solid #DC2626' }}
                    data-testid={`remove-secondary-${em}`}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleAddSecondaryEmail} className="flex gap-2 flex-wrap">
            <input
              type="email"
              value={newSecEmail}
              onChange={(e) => setNewSecEmail(e.target.value)}
              placeholder="another-email@example.com"
              className="flex-1 min-w-[200px] px-4 py-2 rounded-lg outline-none"
              style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' }}
              data-testid="new-secondary-email-input"
              required
            />
            <button type="submit" disabled={addingSecEmail}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold hover:scale-105"
              style={{ backgroundColor: '#1E3A8A', color: 'white' }}
              data-testid="add-secondary-email-btn">
              <Plus size={16} /> {addingSecEmail ? 'Adding...' : 'Add Email'}
            </button>
          </form>
        </div>

        {/* Sub-Admins Section */}
        <div className="rounded-2xl p-6 mb-8" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>
                Sub-Admins <span className="text-lg font-normal" style={{ color: '#8197A4' }}>({subAdmins.length})</span>
              </h2>
              <p className="text-sm mt-1" style={{ color: '#8197A4' }}>These admins can manage movies, users, and featured content</p>
            </div>
            <button onClick={() => setShowAdminModal(true)}
              className="flex items-center gap-2 px-4 py-3 rounded-lg font-semibold hover:scale-105"
              style={{ backgroundColor: '#1E3A8A', color: 'white', boxShadow: '0 4px 15px rgba(30, 58, 138, 0.35)' }}
              data-testid="add-sub-admin-button">
              <UserPlus size={18} /> Add Sub-Admin
            </button>
          </div>

          {subAdmins.length === 0 ? (
            <div className="text-center py-12 rounded-xl" style={{ backgroundColor: '#0F171E', border: '1px dashed #2A3F52' }}>
              <Shield size={40} className="mx-auto mb-3" style={{ color: '#8197A4' }} />
              <p className="font-semibold mb-1" style={{ color: '#FFFFFF' }}>No sub-admins yet</p>
              <p className="text-sm" style={{ color: '#8197A4' }}>Add sub-admins to help you manage the platform</p>
            </div>
          ) : (
            <div className="space-y-3">
              {subAdmins.map(admin => (
                <div key={admin._id} className="p-4 rounded-lg flex items-center justify-between flex-wrap gap-3"
                  style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52' }}
                  data-testid={`sub-admin-${admin._id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                      style={{ backgroundColor: '#3B82F6', color: 'white' }}>
                      {admin.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold" style={{ color: '#FFFFFF' }}>{admin.name}</h4>
                        <span className="px-2 py-0.5 text-xs font-bold rounded-full"
                          style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#3B82F6' }}>
                          SUB-ADMIN
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Mail size={12} style={{ color: '#8197A4' }} />
                        <span className="text-sm" style={{ color: '#DDDDDD' }}>{admin.email}</span>
                      </div>
                      <p className="text-xs mt-1" style={{ color: '#8197A4' }}>
                        {admin.membership_id} • Created {new Date(admin.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteAdmin(admin._id, admin.name)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444', border: '1px solid #DC2626' }}
                    data-testid={`delete-sub-admin-${admin._id}`}>
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Members Section */}
        <div className="rounded-2xl p-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>
                Members <span className="text-lg font-normal" style={{ color: '#8197A4' }}>({members.length})</span>
              </h2>
              <p className="text-sm mt-1" style={{ color: '#8197A4' }}>Create and manage member accounts</p>
            </div>
            <button onClick={() => setShowMemberModal(true)}
              className="flex items-center gap-2 px-4 py-3 rounded-lg font-semibold hover:scale-105"
              style={{ backgroundColor: '#1E3A8A', color: 'white', boxShadow: '0 4px 15px rgba(30, 58, 138, 0.35)' }}
              data-testid="add-member-button">
              <UserPlus size={18} /> Add Member
            </button>
          </div>

          {members.length === 0 ? (
            <div className="text-center py-12 rounded-xl" style={{ backgroundColor: '#0F171E', border: '1px dashed #2A3F52' }}>
              <Users size={40} className="mx-auto mb-3" style={{ color: '#8197A4' }} />
              <p className="font-semibold mb-1" style={{ color: '#FFFFFF' }}>No members yet</p>
              <p className="text-sm" style={{ color: '#8197A4' }}>Create the first member account</p>
            </div>
          ) : (
            <div className="space-y-3">
              {members.map(m => (
                <div key={m._id} className="p-4 rounded-lg flex items-center justify-between flex-wrap gap-3"
                  style={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52' }}
                  data-testid={`member-${m._id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                      style={{ backgroundColor: '#10B981', color: 'white' }}>
                      {m.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold" style={{ color: '#FFFFFF' }}>{m.name}</h4>
                        <span className="px-2 py-0.5 text-xs font-bold rounded-full"
                          style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981' }}>
                          MEMBER
                        </span>
                        {m.status !== 'active' && (
                          <span className="px-2 py-0.5 text-xs font-bold rounded-full"
                            style={{ backgroundColor: 'rgba(220, 38, 38, 0.2)', color: '#EF4444' }}>
                            {(m.status || '').toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap text-sm" style={{ color: '#DDDDDD' }}>
                        {m.email && <span className="flex items-center gap-1"><Mail size={12} style={{ color: '#8197A4' }} /> {m.email}</span>}
                        <span style={{ color: '#8197A4' }}>{m.membership_id}</span>
                        <span style={{ color: '#8197A4' }}>Age {m.age}</span>
                      </div>
                      <p className="text-xs mt-1" style={{ color: '#8197A4' }}>
                        Created {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                        {m.last_login ? ` • Last login ${new Date(m.last_login).toLocaleDateString()}` : ''}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteMember(m._id, m.name)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444', border: '1px solid #DC2626' }}
                    data-testid={`delete-member-${m._id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <CreateSubAdminModal isOpen={showAdminModal} onClose={() => setShowAdminModal(false)} onCreated={fetchAdmins} />
        <CreateMemberModal isOpen={showMemberModal} onClose={() => setShowMemberModal(false)} onCreated={fetchMembers} />
      </div>
    </div>
  );
};
